// src/utils/menuItemHelpers.ts

import { FOOD_TAGS } from './foodTags';
import { MenuItem } from './supabaseClient';
import { ArTranslation } from '../types/custom';

/**
 * Get the localized name for a menu item based on current language
 * @param item - The menu item object
 * @param language - Current language ('en' or 'ar')
 * @returns Localized menu item name
 */
export const getItemName = (item: MenuItem, language: string): string => {
	const arTranslation = item.ar as ArTranslation | null;
	if (language === 'ar' && arTranslation?.name) {
		return arTranslation.name;
	}
	return item.name || '';
};

/**
 * Get the localized description for a menu item based on current language
 * @param item - The menu item object
 * @param language - Current language ('en' or 'ar')
 * @returns Localized menu item description
 */
export const getItemDescription = (item: MenuItem, language: string): string | null => {
	const arTranslation = item.ar as ArTranslation | null;
	if (language === 'ar' && arTranslation?.description) {
		return arTranslation.description;
	}
	return item.description || null;
};

/**
 * Get the localized ingredients for a menu item based on current language
 * @param item - The menu item object
 * @param language - Current language ('en' or 'ar')
 * @returns Localized menu item ingredients array
 */
export const getItemIngredients = (item: MenuItem, language: string): string[] => {
	const arTranslation = item.ar as ArTranslation | null;
	if (language === 'ar' && arTranslation?.ingredients && arTranslation.ingredients.length > 0) {
		return arTranslation.ingredients;
	}
	// Some places in the app add `ingredients` dynamically (not part of default DB row type).
	// Safely cast as any here to avoid TS errors and fall back to an empty array if not present.
	return (item as any).ingredients || [];
};

export const getItemPrice = (item: MenuItem) => {
	return (item?.discounted_price && item?.discounted_price > 0) ? item?.discounted_price : (item?.price ?? 0)
}

// export const getTagName = (tag: typeof FOOD_TAGS[0], language: string): string => {
// 	if (language === 'ar') {
// 		return tag.labelAr;
// 	}
// 	return tag.labelEn;
// };

/**
 * Check if menu item has Arabic translation
 * @param item - The menu item object
 * @returns Boolean indicating whether the item has any Arabic content
 */
export const hasArabicTranslation = (item: MenuItem): boolean => {
	const arTranslation = item.ar as ArTranslation | null;
	return !!(arTranslation?.name || arTranslation?.description || (arTranslation?.ingredients && arTranslation.ingredients.length > 0));
};